import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  ChevronLeft, ChevronRight, MapPin, FileText, Clock,
  Edit2, Trash2, Download, CheckSquare, X, ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { SkeletonAgendaItem } from '../components/Skeleton';
import { toDateStr, formatTanggalID, dateToStr, todayStr } from '../utils/dateUtils';

const HARI = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
const BULAN_NAMA = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const formatJam = j => j ? j.slice(0, 5) : '--:--';

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const days = [];
  for (let i = firstDay - 1; i >= 0; i--) days.push({ date: new Date(year, month - 1, daysInPrev - i), current: false });
  for (let d = 1; d <= daysInMonth; d++) days.push({ date: new Date(year, month, d), current: true });
  const rem = 42 - days.length;
  for (let d = 1; d <= rem; d++) days.push({ date: new Date(year, month + 1, d), current: false });
  return days;
}

/* ─── Modal Detail Jadwal ─── */
function DetailModal({ jadwal, onClose, onEdit, onDelete, onDownload, isAdmin }) {
  if (!jadwal) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ background: jadwal.is_gubernur ? '#FFFBEB' : 'white', borderBottom: `3px solid ${jadwal.is_gubernur ? '#F59E0B' : 'var(--primary)'}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', background: 'var(--primary-bg)', color: 'var(--primary)', borderRadius: 100, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Clock size={11} /> {formatJam(jadwal.jam_mulai)} – {jadwal.is_selesai_tbd ? 'Selesai' : formatJam(jadwal.jam_selesai)}
              </span>
              {jadwal.is_gubernur == 1 && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', background: '#FEF3C7', color: '#92400E', borderRadius: 100 }}>⭐ Gubernur</span>}
              {jadwal.is_disposisi == 1 && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', background: 'var(--secondary-bg)', color: '#065F46', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 3 }}><CheckSquare size={10} /> Disposisi</span>}
            </div>
            <div className="modal-title" style={{ fontSize: 18, lineHeight: 1.3 }}>{jadwal.nama_acara}</div>
            <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 4 }}>
              {formatTanggalID(jadwal.tanggal, { weekday: 'long' })}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: '4px 8px', flexShrink: 0 }} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Penyelenggara</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{jadwal.penyelenggara || '—'}</div>
            </div>
            <div style={{ padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Lokasi</div>
              <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <MapPin size={13} color="var(--gray-400)" /> {jadwal.lokasi || '—'}
              </div>
            </div>
          </div>

          {/* No Surat */}
          {jadwal.no_surat && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={14} color="var(--gray-400)" />
              <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>No. Surat:</span>
              {jadwal.lampiran ? (
                <button onClick={() => onDownload(jadwal)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 13, padding: 0, fontFamily: 'inherit', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'underline' }}>
                  {jadwal.no_surat} <Download size={13} />
                </button>
              ) : (
                <span style={{ fontSize: 13, fontWeight: 600 }}>{jadwal.no_surat}</span>
              )}
            </div>
          )}

          {/* Keterangan - render HTML dari Quill */}
          {jadwal.keterangan && jadwal.keterangan !== '<p><br></p>' && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Keterangan</div>
              <div style={{ whiteSpace: "pre-line" }}>{jadwal.keterangan}</div>
            </div>
          )}

          {/* Disposisi */}
          {jadwal.is_disposisi == 1 && (
            <div style={{ padding: '12px 14px', background: 'var(--secondary-bg)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <CheckSquare size={16} color="var(--secondary)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#065F46' }}>Disposisi</div>
                {jadwal.disposisi_ket && <div style={{ fontSize: 13, color: '#065F46', marginTop: 2 }}>{jadwal.disposisi_ket}</div>}
              </div>
            </div>
          )}

          {/* Lampiran */}
          {jadwal.lampiran && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Paperclip size={14} color="var(--gray-400)" />
              <button onClick={() => onDownload(jadwal)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 13, padding: 0, fontFamily: 'inherit', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                Unduh Lampiran <Download size={13} />
              </button>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="modal-footer">
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(jadwal)}>
              <Trash2 size={14} /> Hapus
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => onEdit(jadwal.id)}>
              <Edit2 size={14} /> Edit Jadwal
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Diperlukan untuk DetailModal
function Paperclip({ size, color }) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>;
}

export default function LihatJadwalPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const todayYMD = todayStr();
  const todayDate = new Date();

  const [viewYear, setViewYear] = useState(todayDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(todayDate.getMonth());
  const [selectedDate, setSelectedDate] = useState(todayYMD);
  const [eventDates, setEventDates] = useState(new Set());
  const [agenda, setAgenda] = useState([]);
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [detailJadwal, setDetailJadwal] = useState(null);

  const calendarDays = buildCalendarDays(viewYear, viewMonth);

  const fetchEventDates = useCallback(() => {
    api.get('/jadwal/dates-with-events', { params: { bulan: viewMonth + 1, tahun: viewYear } })
      .then(res => setEventDates(new Set(res.data.data.map(d => toDateStr(d)))))
      .catch(console.error);
  }, [viewMonth, viewYear]);

  useEffect(() => { fetchEventDates(); }, [fetchEventDates]);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingAgenda(true);
    api.get(`/jadwal/date/${selectedDate}`)
      .then(res => setAgenda(res.data.data))
      .catch(console.error)
      .finally(() => setLoadingAgenda(false));
  }, [selectedDate]);

  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };
  const goToday = () => { setViewMonth(todayDate.getMonth()); setViewYear(todayDate.getFullYear()); setSelectedDate(todayYMD); };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/jadwal/${deleteTarget.id}`);
      toast.success('Jadwal berhasil dihapus');
      setAgenda(prev => prev.filter(j => j.id !== deleteTarget.id));
      fetchEventDates();
      setDeleteTarget(null);
      setDetailJadwal(null);
    } catch { toast.error('Gagal menghapus jadwal'); }
    finally { setDeleting(false); }
  };

  const handleDownloadLampiran = async (jadwal) => {
    try {
      const res = await api.get(`/jadwal/${jadwal.id}/lampiran`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = jadwal.lampiran; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Gagal mengunduh lampiran'); }
  };

  const selectedLabel = selectedDate ? formatTanggalID(selectedDate, { weekday: 'long' }) : '';

  return (
    <div className="page-container">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 24 }} onClick={() => navigate('/')}>
        <ChevronLeft size={16} /> Kembali
      </button>

      <div className="page-header">
        <h1 className="page-title">Lihat Jadwal Pimpinan</h1>
        <p className="page-subtitle">Kalender jadwal kegiatan pimpinan — klik agenda untuk detail</p>
      </div>

      <div className="calendar-layout">
        {/* Kalender */}
        <div className="card">
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={prevMonth}><ChevronLeft size={18} /></button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700 }}>{BULAN_NAMA[viewMonth]} {viewYear}</h2>
                <button className="btn btn-outline btn-sm" onClick={goToday} style={{ fontSize: 12 }}>Hari Ini</button>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={nextMonth}><ChevronRight size={18} /></button>
            </div>
            <div className="calendar-grid" style={{ marginBottom: 8 }}>
              {HARI.map(h => <div key={h} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--gray-400)', padding: '4px 0' }}>{h}</div>)}
            </div>
            <div className="calendar-grid">
              {calendarDays.map((item, i) => {
                const ymd = dateToStr(item.date);
                return (
                  <div key={i}
                    className={['calendar-day', ymd === selectedDate ? 'selected' : '', ymd === todayYMD && ymd !== selectedDate ? 'today' : '', !item.current ? 'other-month' : '', eventDates.has(ymd) ? 'has-event' : ''].join(' ')}
                    onClick={() => setSelectedDate(ymd)} style={{ padding: '8px 0' }}>
                    {item.date.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Panel Agenda */}
        <div className="card agenda-panel">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, background: 'var(--primary-bg)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="var(--primary)" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Agenda</div>
                <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{selectedLabel}</div>
              </div>
            </div>
            {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/input?tanggal=${selectedDate}`)}>+ Tambah</button>
            )}
          </div>

          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 560, overflowY: 'auto' }}>
            {loadingAgenda ? Array(3).fill(0).map((_, i) => <SkeletonAgendaItem key={i} />) :
              agenda.length === 0 ? (
                <div className="empty-state" style={{ padding: '40px 20px' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
                  <p style={{ fontSize: 14 }}>Tidak ada agenda pada tanggal ini</p>
                </div>
              ) : (
                agenda.map(j => (
                  /* ── Klik seluruh card → buka detail modal ── */
                  <div key={j.id}
                    className="agenda-item"
                    onClick={() => setDetailJadwal(j)}
                    style={{
                      background: j.is_gubernur ? '#FFFBEB' : 'white',
                      borderLeft: `3px solid ${j.is_gubernur ? '#F59E0B' : 'var(--primary)'}`,
                      cursor: 'pointer',
                      transition: 'box-shadow 0.15s, transform 0.1s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
                  >
                    {/* Baris atas: jam + badge + aksi */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', background: 'var(--primary-bg)', color: 'var(--primary)', borderRadius: 100, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={10} /> {formatJam(j.jam_mulai)} – {j.is_selesai_tbd ? 'Selesai' : formatJam(j.jam_selesai)}
                        </span>
                        {j.is_gubernur == 1 && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', background: '#FEF3C7', color: '#92400E', borderRadius: 100 }}>⭐ Gubernur</span>}
                        {j.is_disposisi == 1 && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', background: 'var(--secondary-bg)', color: '#065F46', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 3 }}><CheckSquare size={9} /> Disposisi</span>}
                      </div>
                      {/* Tombol aksi — stopPropagation agar tidak trigger detail modal */}
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        {isAdmin && <button className="btn btn-ghost btn-sm" style={{ padding: '4px 7px' }} onClick={() => navigate(`/input?edit=${j.id}`)} title="Edit"><Edit2 size={13} /></button>}
                        {j.lampiran && <button className="btn btn-ghost btn-sm" style={{ padding: '4px 7px', color: 'var(--primary)' }} onClick={() => handleDownloadLampiran(j)} title="Unduh"><Download size={13} /></button>}
                        {isAdmin && <button className="btn btn-ghost btn-sm" style={{ padding: '4px 7px', color: 'var(--red)' }} onClick={() => setDeleteTarget(j)} title="Hapus"><Trash2 size={13} /></button>}
                      </div>
                    </div>

                    {/* Nama acara */}
                    <div style={{ fontWeight: 700, fontSize: 15, marginTop: 8 }}>{j.nama_acara}</div>

                    {/* Penyelenggara */}
                    {j.penyelenggara && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>{j.penyelenggara}</div>}

                    {/* Lokasi */}
                    {j.lokasi && (
                      <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin size={11} /> {j.lokasi}
                      </div>
                    )}

                    {/* Keterangan preview (plain text dari HTML) */}
                    {j.keterangan && j.keterangan !== '<p><br></p>' && (
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, color: 'var(--gray-400)', marginTop: 5, fontStyle: 'italic' }}>{j.keterangan}</div>
                    )}

                    {/* No Surat */}
                    {j.no_surat && (
                      <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FileText size={11} /> {j.no_surat}
                        {j.lampiran && <Download size={11} color="var(--primary)" />}
                      </div>
                    )}
                  </div>
                ))
              )}
          </div>
        </div>
      </div>

      {/* Modal Detail */}
      <DetailModal
        jadwal={detailJadwal}
        onClose={() => setDetailJadwal(null)}
        onEdit={id => { setDetailJadwal(null); navigate(`/input?edit=${id}`); }}
        onDelete={j => { setDetailJadwal(null); setDeleteTarget(j); }}
        onDownload={handleDownloadLampiran}
        isAdmin={isAdmin}
      />

      <ConfirmModal show={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting}
        title="Hapus Jadwal?" message={`Jadwal "${deleteTarget?.nama_acara}" akan dipindahkan ke arsip.`} confirmLabel="Ya, Hapus" />
    </div>
  );
}
